// src/components/ui/dropDownMenu/DropDownMenu.tsx
'use client';

import React from 'react';
import {
  // IconBlockOutline,
  IconDelete,
  IconEditOutline,
  IconFavorOutline,
  IconFavorSolid,
  IconReportOutline,
  IconShareOutline,
} from '@/components/icons';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import style from './DropDownMenu.module.scss';
import { useRouter } from 'next/navigation';
import { useAppSelector } from '@/store/hook';

type MeInfoLike = {
  womo_id?: string;
  womoId?: string;
  user_id?: string | number; // 通常這個會對應 kol_id
  userId?: string | number;
  owner_open_id?: string;
  owner_id?: string;
};

type DropDownMenuProps = {
  trigger: React.ReactElement;

  /** 來自 API 的 is_edit：true 才有機會顯示「編輯 / 刪除」 */
  canEdit?: boolean;

  /** 文章擁有者的 kol_id（用來跟 meInfo 做比對） */
  ownerKolId?: string | number;

  /** 初始 / 目前蒐藏狀態（用來切換 outline / solid） */
  isFav?: boolean;

  /** 未登入時導向的登入頁（請依專案路由調整） */
  loginHref?: string;

  /** 動作 callback（可選） */
  onFavor?: () => void | Promise<void>;
  onEdit?: () => void | Promise<void>;
  onShare?: () => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  onBlock?: () => void | Promise<void>;
  onReport?: () => void | Promise<void>;

  showShare?: boolean;
  showFavor?: boolean;
  showReport?: boolean;

  width?: string;
  className?: string;
};

const DropDownMenu = ({
  trigger,
  ownerKolId,
  canEdit,
  isFav,
  loginHref = '/auth/login',
  onFavor,
  onEdit,
  onShare,
  onDelete,
  // onBlock, // 目前 UI 沒開封鎖項目，所以不解構以避免 unused
  onReport,
  showShare = true,
  showFavor = true,
  showReport = true,
  width = '100px',
  className,
}: DropDownMenuProps) => {
  const router = useRouter();

  // 一律用全域 meInfo 判斷是否登入
  const meInfo = useAppSelector((state) => state.auth.meInfo) as MeInfoLike | null;
  const isLoggedIn = !!meInfo;

  // 取出「目前登入者的 id」（對應 kol_id）
  const meKolIdRaw = meInfo?.user_id ?? meInfo?.userId;
  const meKolId = meKolIdRaw != null ? String(meKolIdRaw) : '';

  // 文章/空間擁有者的 id
  const ownerKolIdStr = ownerKolId != null ? String(ownerKolId) : '';

  // 是否為本人：kol_id === meInfo
  const isOwner = !!meKolId && !!ownerKolIdStr && meKolId === ownerKolIdStr;
  const canEditFlag = !!canEdit;

  // 不需要登入的動作（例如純分享 / 開 ShareModal）
  const handlePlain =
    (cb?: () => void | Promise<void>) =>
    (event: Event): void => {
      event?.preventDefault?.();
      void cb?.();
    };

  // 需要登入的動作：未登入 → 跳登入頁，已登入 → 執行 callback
  const requireLogin =
    (cb?: () => void | Promise<void>) =>
    (event: Event): void => {
      event?.preventDefault?.();

      if (!isLoggedIn) {
        if (!loginHref) return;

        if (typeof window !== 'undefined') {
          const current = window.location.pathname + window.location.search;

          const hasQuery = loginHref.includes('?');
          const loginWithRedirect = `${loginHref}${
            hasQuery ? '&' : '?'
          }redirect=${encodeURIComponent(current)}`;

          window.location.href = loginWithRedirect;
        } else {
          router.push(loginHref);
        }
        return;
      }

      void cb?.();
    };

  // ===========================
  // 預設分享行為：沒傳 onShare 時使用
  // ===========================
  const defaultShare = () => {
    if (typeof window === 'undefined') return;
    const url = window.location.href;
    const title = document.title || 'WOMO';

    // 幫 navigator 補上可能的 share 型別，而不是 any
    const nav = navigator as Navigator & {
      share?: (data: { title?: string; url?: string }) => Promise<void>;
    };

    try {
      if (nav.share) {
        nav.share({ title, url }).catch(() => {
          // 使用者取消 share，不需要特別處理
        });
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(
          () => {
            alert('已複製分享連結');
          },
          () => {
            alert('分享連結複製失敗，請手動複製網址');
          }
        );
      } else {
        alert('目前瀏覽器不支援分享功能，請手動複製網址');
      }
    } catch (err) {
      console.warn('[DropDownMenu] share failed:', err);
      alert('分享失敗，請稍後再試或手動複製網址');
    }
  };

  const effectiveOnShare = onShare ?? defaultShare;

  const favorIcon = isFav ? <IconFavorSolid width={21} /> : <IconFavorOutline width={22} />;
  const favorText = isFav ? '取消蒐藏' : '蒐藏';

  //是否要顯示「編輯 / 刪除」（已登入，而且 (是本人 OR canEdit 為 true)）
  const canShowOwnerActionsBase = isLoggedIn && (isOwner || canEditFlag);
  const canShowEdit = canShowOwnerActionsBase && !!onEdit;
  const canShowDelete = canShowOwnerActionsBase && !!onDelete;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>

      <DropdownMenu.Content
        className={`rounded border bg-white mt-2 me-3 shadow text-muted ${className}`}
        style={{ minWidth: width, zIndex: 100 }}
      >
        {/* =============================
            蒐藏：需要登入 + icon 依 isFav 切換
           ============================= */}
        {showFavor && (
          <DropdownMenu.Item
            className={`px-3 py-2 fs-sm-1 ${style.menuItem}`}
            style={{ cursor: 'pointer' }}
            onSelect={requireLogin(onFavor)}
          >
            <span className="me-2">{favorIcon}</span>
            {favorText}
          </DropdownMenu.Item>
        )}

        {/* =============================
            分享：不強制登入（開 ShareModal / Web Share API）
           ============================= */}
        {showShare && (
          <DropdownMenu.Item
            className={`px-3 py-2 fs-sm-1 ${style.menuItem}`}
            style={{ cursor: 'pointer' }}
            onSelect={handlePlain(effectiveOnShare)}
          >
            <span className="me-2">
              <IconShareOutline />
            </span>
            分享
          </DropdownMenu.Item>
        )}

        {/* =============================
            編輯：要登入 + 是本人 OR canEdit 為 true + 有 onEdit
           ============================= */}
        {canShowEdit && (
          <DropdownMenu.Item
            className={`px-3 py-2 fs-sm-1 ${style.menuItem}`}
            style={{ cursor: 'pointer' }}
            onSelect={requireLogin(onEdit)}
          >
            <span className="me-2">
              <IconEditOutline />
            </span>
            編輯
          </DropdownMenu.Item>
        )}

        {/* =============================
            刪除：要登入 + 是本人 OR canEdit 為 true + 有 onDelete
           ============================= */}
        {canShowDelete && (
          <DropdownMenu.Item
            className={`px-3 py-2 fs-sm-1 ${style.menuItem}`}
            style={{ cursor: 'pointer' }}
            onSelect={requireLogin(onDelete)}
          >
            <span className="me-2">
              <IconDelete />
            </span>
            刪除
          </DropdownMenu.Item>
        )}

        {/* =============================
            封鎖 / 檢舉：需要登入（不限是不是本人貼文）
           ============================= */}
        {/* 之後要開封鎖功能再解註：
        <DropdownMenu.Item
          className={`px-3 py-2 fs-sm-1 text-danger border-top ${style.menuItem}`}
          style={{ cursor: 'pointer' }}
          onSelect={requireLogin(onBlock)}
        >
          <span className="me-2">
            <IconBlockOutline />
          </span>
          封鎖此人的貼文
        </DropdownMenu.Item>
        */}
        {showReport && (
          <DropdownMenu.Item
            className={`px-3 py-2 fs-sm-1 text-danger border-top ${style.menuItem}`}
            style={{ cursor: 'pointer' }}
            onSelect={requireLogin(onReport)}
          >
            <span className="me-2">
              <IconReportOutline />
            </span>
            檢舉
          </DropdownMenu.Item>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default DropDownMenu;
